import { TEMPORARY_CELL } from "../components/cell/grid-cell.config";
import { GridCell } from "../components/cell/grid-cell";
import { GRID_DIRECTION } from "../const/grid-directions.const";
import { CellRetrievalUtils } from "./CellRetrievalUtils";

export class ExpandingViewUtils {
    cellRetrieval = new CellRetrievalUtils();

    /**
     * @description Calculates the size of the temporary grid
     * @param {Object} pos - Mouse position {x:0,y:0}
     * @param {Object} cellOptions - Cell Options
     * @param {Array[]} grid - GeneratedGrid
     * @returns {Object} {size, dir} - Size is the size of the generated temporary grid. Dir is the direction the grid is being genenator to
     */
    calculateSizeToGenerate(pos, cellOptions, grid, dir, _self) {
        let min = {};
        if (pos.hasOwnState && pos.height && pos.width) {
            min = (function () {
                const coveredCells = _self.cellIndependentPluginHandler.getCoveredCells({ x: pos.x, y: pos.y }, { width: pos.width, height: pos.height }, grid, true, true);
                const gridEndRow = grid.length - 1;
                const gridEndCol = grid[0].length - 1;
                const startPointPos = _self.utils.cellRetrieval.getCellPosition(pos, null, grid);
                const recordedColsCounter = [];
                const recordedRowsCounter = [];
                return coveredCells.reduce((counter, value) => {
                    const cell = _self.utils.cellRetrieval.getCell(value, grid);
                    let setCorner;
                    if (!cell && (value.col < 0 || value.col > gridEndCol) && recordedColsCounter.indexOf(value.col) < 0) {
                        setCorner = false;
                        if (counter.dir && (counter.dir === GRID_DIRECTION.TOP || counter.dir === GRID_DIRECTION.BOTTOM)) {
                            setCorner = true;
                        }
                        if (value.col < 0 && (!counter.dir || (counter.dir && setCorner))) {
                            if (setCorner) {
                                counter.dir = GRID_DIRECTION[`${counter.dir}_${GRID_DIRECTION.LEFT}`];
                            } else counter.dir = GRID_DIRECTION.LEFT;
                        }
                        if (value.col > gridEndCol && (!counter.dir || (counter.dir && setCorner))) {
                            if (setCorner) {
                                counter.dir = GRID_DIRECTION[`${counter.dir}_${GRID_DIRECTION.RIGHT}`];
                            } else counter.dir = GRID_DIRECTION.RIGHT;
                        }
                        recordedColsCounter.push(value.col);
                        counter.leavingHorizontal += 1;
                    }
                    if (!cell && (value.row < 0 || value.row > gridEndRow) && recordedRowsCounter.indexOf(value.row) < 0) {
                        setCorner = false;
                        if (counter.dir && (counter.dir === GRID_DIRECTION.LEFT || counter.dir === GRID_DIRECTION.RIGHT)) {
                            setCorner = true;
                        }
                        if (value.row < 0 && (!counter.dir || (counter.dir && setCorner))) {
                            if (setCorner) {
                                counter.dir = GRID_DIRECTION[`${GRID_DIRECTION.TOP}_${counter.dir}`];
                            } else counter.dir = GRID_DIRECTION.TOP;
                        }
                        if (value.row > gridEndRow && (!counter.dir || (counter.dir && setCorner))) {
                            if (setCorner) {
                                counter.dir = GRID_DIRECTION[`${GRID_DIRECTION.BOTTOM}_${counter.dir}`];
                            } else counter.dir = GRID_DIRECTION.BOTTOM;
                        }
                        recordedRowsCounter.push(value.row);
                        counter.leavingVertical += 1;
                    }
                    return counter;
                }, { leavingVertical: 0, leavingHorizontal: 0, dir: null });
            }());

            min = {
                height: (min.leavingVertical * cellOptions.cellHeight),
                width: (min.leavingHorizontal * cellOptions.cellWidth),
                dir: min.dir
            };
            if (min.width !== 0) min.width += cellOptions.cellWidth;
            if (min.height !== 0) min.height += cellOptions.cellHeight;
        }
        const distanceToClosestCell = this.getDistanceToClosestCell(pos, cellOptions, grid, dir);
        if (distanceToClosestCell) {
            if (distanceToClosestCell.width < 0) distanceToClosestCell.width = Math.abs(distanceToClosestCell.width);
            if (distanceToClosestCell.height < 0) distanceToClosestCell.height = Math.abs(distanceToClosestCell.height);
        }
        const cellsToGenerate = {
            width: distanceToClosestCell.width ? (Math.ceil(distanceToClosestCell.width / cellOptions.cellWidth) * cellOptions.cellWidth + (cellOptions.cellWidth)) : 0,
            height: distanceToClosestCell.height ? (Math.ceil(distanceToClosestCell.height / cellOptions.cellHeight) * cellOptions.cellHeight + (cellOptions.cellHeight)) : 0
        };
        if (min) {
            if (cellsToGenerate.width < min.width) cellsToGenerate.width = min.width;
            if (cellsToGenerate.height < min.height) cellsToGenerate.height = min.height;
        }
        return { size: cellsToGenerate, dir: min.dir };
    }

    /**
     * @description Gets the size negative value of width and height (100 will be -100)
     * @param {Object} size - {width, height}
     * @returns {Object} {width, height}
     */
    getTemporaryNegativeSize(size) {
        return {
            width: size.width ? (size.width - (size.width * 2)) : size.width,
            height: size.height ? (size.height - (size.height * 2)) : size.height
        };
    }

    /**
     * @description Called from generateAditionalRows method of the same class. Returns the x and y of the first cell in the provided currentRow property
     * @param {String} dir - The direction of the grid
     * @param {Array[]} grid - Generated grid matrix
     * @param {Number} rows - Total Number of rows
     * @param {Number} currentRow - The
     * @param {Object} options - CellConfig object containing cellHeight and cellWidth
     * @param {Object} pos - {x,y} current mouse position
     * @return {object} - { currentx, currentY }
     */
    getRowPositionBasedOnDirection(dir, grid, rows, currentRow, options, pos) {
        const gridStartY = grid[0][0].layout.y;
        let gridStartX;
        if (pos === GRID_DIRECTION.TOP_RIGHT || pos === GRID_DIRECTION.BOTTOM_RIGHT) {
            gridStartX = grid[0][0].layout.x;
        } else {
            gridStartX = options.viewPadding;
        }
        // This returns an aditional options.height. Will be used as the starting point for generating the grid in case is bottom
        const currentRowIncrease = currentRow * options.cellHeight;
        const gridEndY = gridStartY + (grid.length * options.cellHeight) + currentRowIncrease;
        const returnVal = {
            currentX: gridStartX,
            currentY: null
        };
        returnVal.currentY = dir === GRID_DIRECTION.BOTTOM ? gridEndY : (gridStartY - rows * options.cellHeight + currentRowIncrease);
        return returnVal;
    }

    /**
     * @description Generates the addintional rows needed for the temporary grid
     * @param {Number} rows - total number of rows
     * @param {Array[]} grid  - Generated grid matrix
     * @param {Object} pos - Current mouse positiion
     * @param {Number} rowLength - Row length
     * @param {GridConfig} options - cell config object
     * @param {CanvasRenderingContext2D} canvasCtx - canvas 2d context
     * @param {LabelHandler} labelHandler - LabelHandler instance
     * @returns { GridCell[] } - returns a reference to all the newly created GridCells
     */
    generateAditionalRows(rows, grid, pos, rowLength, options, canvasCtx, labelHandler) {
        const position = pos === GRID_DIRECTION.BOTTOM_RIGHT || pos === GRID_DIRECTION.BOTTOM_LEFT || pos === GRID_DIRECTION.BOTTOM ? GRID_DIRECTION.BOTTOM : GRID_DIRECTION.TOP;
        const allRows = [];
        const allCellsRefs = [];
        for (let row = 0; row < rows; row++) {
            const newRow = [];
            const coords = this.getRowPositionBasedOnDirection(position, grid, rows, row, options, pos);
            for (let cell = 0; cell < rowLength; cell++) {
                // TODO: Calculate radius
                const radius = 0;
                const cellLayout = {
                    x: coords.currentX, y: coords.currentY, width: options.cellWidth, height: options.cellHeight, styles: TEMPORARY_CELL, radius
                };
                const instance = new GridCell(cellLayout, { temporary: true }, canvasCtx, labelHandler);
                // instance.draw();
                newRow.push(instance);
                allCellsRefs.push(instance);
                coords.currentX += options.cellWidth;
            }
            allRows.push(newRow);
        }
        if (position === GRID_DIRECTION.TOP) {
            grid.unshift(...allRows);
        } else if (position === GRID_DIRECTION.BOTTOM) {
            allRows.forEach((x) => {
                grid.push(x);
            });
        }
        return allCellsRefs;
    }

    /**
     * @description Generates aditional cells for temporary grid row
     * @param {Number} cellNr  - Number of cells to be generated
     * @param {Array[]} grid - Generated grid matrix
     * @param {Object} pos - Current Mouse position
     * @param {Number} totalRowWidth - total row width of cells
     * @param {GridConfig} options - cell options object
     * @param {CanvasRenderingContext2D} canvasCtx - Canvas 2d ctx
     * @param {LabelHandler} labelHandler - Label Handler instance
     * @returns {GridCell[]} - An array of newly generated GridCell references
     */
    generateAditionalCells(cellNr, grid, pos, totalRowWidth, options, canvasCtx, labelHandler) {
        const position = pos === GRID_DIRECTION.BOTTOM_RIGHT || pos === GRID_DIRECTION.TOP_RIGHT || pos === GRID_DIRECTION.RIGHT ? GRID_DIRECTION.RIGHT : GRID_DIRECTION.LEFT;
        const allNewlyGeneratedCells = [];
        let initialHeight = pos === GRID_DIRECTION.BOTTOM_RIGHT || pos === GRID_DIRECTION.BOTTOM_LEFT;
        if (initialHeight) {
            const currentRow = grid[0];
            let currentLastCell = currentRow[totalRowWidth - cellNr];
            if (!currentLastCell) currentLastCell = currentRow[totalRowWidth - cellNr - 1];
            initialHeight = currentLastCell.layout.y;
        }
        for (let row = 0; row < grid.length; row++) {
            let currentX = options.viewPadding;
            let currentY;
            let cellXIndicator = grid[0][totalRowWidth - cellNr];
            if (cellXIndicator && position === GRID_DIRECTION.RIGHT) {
                currentX = cellXIndicator.layout.x;
            } else if (!cellXIndicator) {
                cellXIndicator = grid[0][totalRowWidth - cellNr - 1];
                if (position === GRID_DIRECTION.RIGHT) currentX = cellXIndicator.layout.x + options.cellWidth;
            }
            currentY = row * options.cellHeight + (initialHeight || options.viewPadding);
            if (currentY === 0 && GRID_DIRECTION.TOP) currentY = options.viewPadding;

            if (grid[row].length === totalRowWidth) {
                continue;
            }
            const aditionalCells = [];
            for (let cell = 0; cell < cellNr; cell++) {
                // TODO: Calculate radius
                const radius = 0;
                const cellLayout = {
                    x: currentX, y: currentY, width: options.cellWidth, height: options.cellHeight, styles: TEMPORARY_CELL, radius
                };
                const instance = new GridCell(cellLayout, { temporary: true }, canvasCtx, labelHandler);
                // instance.draw();
                allNewlyGeneratedCells.push(instance);
                aditionalCells.push(instance);
                currentX += options.cellWidth;
            }
            if (position === GRID_DIRECTION.RIGHT) {
                grid[row].push(...aditionalCells);
            } else {
                grid[row].unshift(...aditionalCells);
            }
        }
        return allNewlyGeneratedCells;
    }

    /**
     * @description Gets the distance to the closest PERMANENT cell based on the temporary grid creation direction
     * @param {Object} pos - {x,y} Current mouse position
     * @param {GridConfig} cellOptions - The gridConfig reference holding cell config properties
     * @param {Array[]} grid -  The generated grid matrix that holds the references to all GridCells
     * @param {String} dir - The direction the temporary grid is generated
     * @returns {width, height}  - the width and height from mouse position to the closest cell
     */
    getDistanceToClosestCell(pos, cellOptions, grid, dir) {
        const firstRow = grid[0];
        const lastRow = grid[grid.length - 1];
        const firstCell = firstRow[0];
        const lastCellFirstRow = firstRow[firstRow.length - 1];
        const lastCellVertical = lastRow[0];
        const left = firstCell.layout.x;
        const right = lastCellFirstRow.layout.x + cellOptions.cellWidth;
        const top = firstCell.layout.y;
        const bottom = lastCellVertical.layout.y + cellOptions.cellHeight;
        const mouse = { ...pos };
        if (dir === GRID_DIRECTION.LEFT) {
            return {
                width: left - mouse.x
            };
        }
        if (dir === GRID_DIRECTION.RIGHT) {
            return {
                width: right - mouse.x
            };
        }
        if (dir === GRID_DIRECTION.TOP) {
            return {
                height: top - mouse.y
            };
        }
        if (dir === GRID_DIRECTION.BOTTOM) {
            return {
                height: bottom - mouse.y
            };
        }
        if (dir === GRID_DIRECTION.BOTTOM_LEFT) {
            return {
                width: left - mouse.x,
                height: bottom - mouse.y
            };
        }
        if (dir === GRID_DIRECTION.BOTTOM_RIGHT) {
            return {
                width: right - mouse.x,
                height: bottom - mouse.y
            };
        }
        if (dir === GRID_DIRECTION.TOP_LEFT) {
            return {
                width: left - mouse.x,
                height: top - mouse.y
            };
        }
        if (dir === GRID_DIRECTION.TOP_RIGHT) {
            return {
                width: right - mouse.x,
                height: top - mouse.y
            };
        }
        return {
            width: 0,
            height: 0
        };
    }

    /**
     * @description - Indicates how much the cells are shifting
     * @param {Object} tempSize - {width,height} temporary grid size
     * @param {String} dir -  the direction of the temporary grid generation
     * @returns  {Object} - {width,height}
     */
    getCurrentCellsMovement(tempSize, dir) {
        const cellMove = {
            width: tempSize.width,
            height: tempSize.height
        };
        if (dir === GRID_DIRECTION.RIGHT || dir === GRID_DIRECTION.TOP_RIGHT || dir === GRID_DIRECTION.BOTTOM_RIGHT) {
            cellMove.width = 0;
        }
        if (dir === GRID_DIRECTION.BOTTOM || dir === GRID_DIRECTION.BOTTOM_RIGHT || dir === GRID_DIRECTION.BOTTOM_LEFT) {
            cellMove.height = 0;
        }
        return cellMove;
    }
}
